// Brand-neutral design tokens. The tenant's own brand colour overrides
// `primary` at runtime (see src/theme/index.js).

export const WHITE = "#FFFFFF";
export const BLACK = "#000000";

/* ---------------------------------- light --------------------------------- */
export const LIGHT_TEXT_PRIMARY = "#333333";
export const LIGHT_TEXT_SECONDARY = "#666666";
export const LIGHT_TEXT_DISABLED = "#777777";

export const LIGHT_PRIMARY_LIGHT = "#552d74";
export const LIGHT_PRIMARY_MAIN = "#431665";
export const LIGHT_PRIMARY_DARK = "#3c135a";
export const LIGHT_PRIMARY_CONTRAST = "#FFFFFF";

export const LIGHT_SECONDARY_LIGHT = "#F5F5F5";
export const LIGHT_SECONDARY_MAIN = "#FFFFFF";
export const LIGHT_SECONDARY_DARK = "#F5F5F5";
export const LIGHT_SECONDARY_CONTRAST = "#000000";

export const LIGHT_BG_PAPER = "#F5F5F5";
export const LIGHT_BG_DEFAULT = "#FFFFFF";
export const LIGHT_DIVIDER = "#DCDCDC";

export const LIGHT_ACTION_HOVER = "#E8E8E8";
export const LIGHT_ACTION_SELECTED = "#66666629";
export const LIGHT_ACTION_DISABLED = "#929292";
export const LIGHT_ACTION_DISABLED_BG = "#DCDCDC";

export const LIGHT_GREY: Record<string, string> = {
  50: "#F5F5F5",
  100: "#EEEEEE",
  200: "#E8E8E8",
  300: "#DCDCDC",
  400: "#D0D0D0",
  500: "#BDBDBD",
  600: "#929292",
  700: "#454F5B",
  800: "#444444",
  900: "#161C24",
  A100: "#66666614",
  A200: "#66666629",
  A400: "#44444452",
  A700: "#333333",
};

/* ---------------------------------- dark ---------------------------------- */
// `#333333` really is the original's dark-mode `text.primary` — 1.4:1 on the
// `#080808` background. Reverted for fidelity; see RECOVERY_PLAN.md.
export const DARK_TEXT_PRIMARY = "#333333";
export const DARK_TEXT_SECONDARY = "#999999";
export const DARK_TEXT_DISABLED = "#666666";

export const DARK_PRIMARY_LIGHT = "#795f82";
export const DARK_PRIMARY_MAIN = "#6B4E75";
export const DARK_PRIMARY_DARK = "#604669";
export const DARK_PRIMARY_CONTRAST = "#FFFFFF";

export const DARK_SECONDARY_LIGHT = "#F5F5F5";
export const DARK_SECONDARY_MAIN = "#E8E8E8";
export const DARK_SECONDARY_DARK = "#F5F5F5";
export const DARK_SECONDARY_CONTRAST = "#000000";

export const DARK_BG_PAPER = "#181818";
export const DARK_BG_DEFAULT = "#080808";
export const DARK_DIVIDER = "#2B2B2B";

export const DARK_ACTION_HOVER = "#444444";
export const DARK_ACTION_DISABLED = "#6C6C6C";
export const DARK_ACTION_DISABLED_BG = "#3333338f";
export const DARK_ACTION_FOCUS = "#33333329";

export const DARK_GREY: Record<string, string> = {
  50: "#F5F5F5",
  100: "#EEEEEE",
  200: "#E8E8E8",
  300: "#DCDCDC",
  400: "#BDBDBD",
  500: "#606060",
  600: "#6C6C6C",
  700: "#454F5B",
  800: "#444444",
  900: "#161C24",
  A100: "#33333314",
  A200: "#33333329",
  A400: "#33333352",
  A700: "#3333338f",
};

/* ------------------------------ status colours ----------------------------- */
export const INFO = {
  light: "#0022FF",
  main: "#0000FF",
  dark: "#2200FF",
  contrastText: "#FFFFFF",
};
export const SUCCESS = {
  light: "#198c19",
  main: "#008000",
  dark: "#007300",
  contrastText: "#FFFFFF",
};
/**
 * Verbatim from the original (`_app` module 47517). An earlier pass re-tuned
 * warning and error for WCAG; that has been reverted so the palette is
 * pixel-identical. The contrast shortfalls this reintroduces are recorded in
 * RECOVERY_PLAN.md under "Original palette — where it falls short of WCAG".
 */
export const WARNING = {
  light: "#e5540c",
  main: "#FF5E0E",
  dark: "#ff6e26",
  contrastText: "#FFFFFF",
};
export const ERROR = {
  light: "#ff3919",
  main: "#FF0000",
  dark: "#e52000",
  contrastText: "#FFFFFF",
};

export const DARK_INFO = {
  light: "#569dff",
  main: "#4493f8",
  dark: "#3d84e5",
  contrastText: "#FFFFFF",
};
/** Verbatim from the original, as above. */
export const DARK_SUCCESS = {
  light: "#198c19",
  main: "#008000",
  dark: "#007300",
  contrastText: "#FFFFFF",
};
export const DARK_WARNING = {
  light: "#d36c26",
  main: "#CF5C0E",
  dark: "#ba520c",
  contrastText: "#FFFFFF",
};
export const DARK_ERROR = {
  light: "#d11919",
  main: "#CC0000",
  dark: "#b70000",
  contrastText: "#FFFFFF",
};

/* --------------------------- store status colours -------------------------- */
export const STATUS_OPEN = "#4caf50";
export const STATUS_BREAK = "#ff9800";
export const STATUS_CLOSED = "#ef5350";
export const STATUS_UNKNOWN = "#9e9e9e";
